/**
 * captureRateLimited — Phase 3 § 3.1 (resilience-and-observability-hardening)
 *
 * Tab-local rate-limit shim for Sentry capture sites that can storm under
 * degenerate conditions. Fires `fn` at most once per 60 seconds per key.
 *
 * ADR 0007: module-scope `Map<string, number>` (NOT useRef, NOT sessionStorage).
 * ES module semantics in a Vite SPA guarantee one Map instance per tab JS heap.
 */

const lastFiredMs = new Map<string, number>();

const RATE_LIMIT_WINDOW_MS = 60_000;

/**
 * Fires `fn` if at least 60 seconds have elapsed since the last fire for this key.
 *
 * Tab-local: the module-scope Map lives in one instance per tab's JS heap (ES module
 * semantics in a Vite SPA). A user with 3 tabs open gets 3 independent rate-limit
 * buckets — correct because each tab has its own failure trajectory.
 *
 * NO localStorage, NO BroadcastChannel — see ADR 0007 + spec § 3.4 for rationale.
 *
 * **DO** pass static keys (e.g. `'realtime_resync_sustained_backoff'`). The map is
 * bounded only when keys are static; dynamic keys (e.g. per-conversation-id) cause
 * unbounded growth in long-lived tabs.
 *
 * @param key — stable identifier for the capture site (static literal preferred).
 * @param fn — the capture call to gate (typically `() => Sentry.captureMessage(...)`).
 */
export function captureRateLimited(key: string, fn: () => void): void {
  const now = Date.now();
  const last = lastFiredMs.get(key) ?? 0;
  if (now - last < RATE_LIMIT_WINDOW_MS) return;
  lastFiredMs.set(key, now);
  fn();
}

/**
 * Test-only reset. NEVER call from production code.
 */
export function __resetCaptureRateLimitedForTests(): void {
  lastFiredMs.clear();
}
